package terraultima.game

import terraultima.*
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.pow
import kotlin.math.roundToInt
import kotlin.random.Random

/**
 * Interface-agnostic game flow. Owns the translation from [WorldState] to view models, and nothing
 * about presentation.
 *
 * It is simultaneously the engine's [Bot] (it asks the frontend what to do) and its [EngineListener]
 * (it tells the frontend what happened). That keeps ONE simulation loop for the played game and the
 * measured harness — if a UI ever gets its own loop, every number in docs/PLAYTEST-LOG.md stops
 * describing the actual game.
 */
class GameSession(
    private val ui: Frontend,
    private val seed: Int? = null,
    private val maxYears: Int = 100
) : Bot, EngineListener, SessionInfo {

    override val name = "player"

    lateinit var state: WorldState
        private set
    private lateinit var engine: Engine
    private var pending: DecisionView? = null
    private var pendingOffered: List<Option> = emptyList()
    private val notes = mutableListOf<String>()
    private val log = mutableListOf<HistoryEntry>()
    private var lastAbsorbed = 0
    private var lastSeverity = Severity.CALM
    private var lastTitle = ""
    private var watching = false

    /**
     * A session does not load the corpus, for two reasons.
     *
     * It used to call `Content.load()` unconditionally, which meant a host running more than one
     * session (the Telegram bot serves a chat per thread) could have one session clear and rebuild
     * `Content`'s indexes while another was reading them. And `Content.load()` reads six files off
     * a disk, which a browser does not have — the engine compiles to JS only because loading is
     * somebody else's job (`ContentFiles.kt` on a JVM, a `fetch` in `web/`).
     *
     * The corpus is immutable once loaded, so loading it once, before any session, is both safe and
     * correct.
     */
    private fun requireCorpus() = check(Content.loaded) {
        "the corpus is not loaded — call Content.load() (JVM) or Content.loadFrom(...) first"
    }

    // ------------------------------------------------------------------ flow
    fun play(): EndingView {
        requireCorpus()
        val motives = Content.corpus.catastrophes
        val views = motives.map { MotiveView(it.id, it.line, it.name, it.teaches, it.detail) }
        ui.attach(this)
        val pick = ui.chooseMotive(views, 0).coerceIn(0, motives.size - 1)
        val cata = motives[pick]

        val rng = Random(seed ?: Random.nextInt(1, 1_000_000))
        state = WorldState(Content.build(cata.build), Content.planet(cata.planet),
            Content.planet(cata.planet).name, cata.name, cata)
        engine = Engine(state, this, rng, maxYears)
        Setup.begin(state, rng)

        ui.reviewSetup(setupView(cata))
        engine.listener = this
        engine.run()
        val e = endingView()
        ui.ending(e)
        return e
    }

    /** Runs a whole century with a policy bot in the seat, rendering to the same frontend. */
    fun watch(policy: String, motiveId: String): EndingView {
        watching = true
        requireCorpus()
        val cata = Content.catastrophe(motiveId)
        val rng = Random(seed ?: 42)
        state = WorldState(Content.build(cata.build), Content.planet(cata.planet),
            Content.planet(cata.planet).name, cata.name, cata)
        ui.attach(this)
        val bot = Bots.all().first { it.name == policy }
        engine = Engine(state, bot, rng, maxYears)
        Setup.begin(state, rng)
        engine.listener = this
        engine.run()
        val e = endingView()
        ui.ending(e)
        return e
    }

    // ------------------------------------------------------------------ Bot
    override fun choose(s: WorldState, e: GameEvent, offered: List<Option>, rng: Random): Option {
        val d = pending ?: return offered.first()
        val n = ui.present(d)
        pending = null
        // The number is a position among the options the player could SEE, not an index into the
        // event's own list: `whenLocked` can keep an option off the screen entirely.
        val chosen = numberOf.entries.firstOrNull { it.value == n }?.key
            ?.takeIf { pendingOffered.contains(it) } ?: offered.first()
        log.add(HistoryEntry(s.year, lastTitle, chosen.label, lastSeverity))
        if (chosen.tags.isNotEmpty()) taggedChoices.add(Triple(s.year, chosen.label, chosen.tags))
        seenBefore[e.id] = (seenBefore[e.id] ?: 0) + 1
        lastChoiceOn[e.id] = chosen.label
        lastYearOn[e.id] = s.year
        return chosen
    }

    // ------------------------------------------------------------------ EngineListener
    /** How many times each event has been ANSWERED before, and what was chosen. */
    private val seenBefore = mutableMapOf<String, Int>()
    private val lastChoiceOn = mutableMapOf<String, String>()
    private val lastYearOn = mutableMapOf<String, Int>()
    private var pendingId = ""

    /** "the third time — you tore it up in year 31." Names the choice, because that is the memory. */
    private fun previously(e: GameEvent): String {
        val n = seenBefore[e.id] ?: return ""
        if (n == 0) return ""
        val ord = when (n) { 1 -> "the second time"; 2 -> "the third time"; 3 -> "the fourth time"
                             else -> "the ${n + 1}th time" }
        // The ORDINAL only. The body now carries the sentence ("22 years ago you faced this, and
        // you answered it with X"), and printing both put the same fact on screen twice — while
        // juxtaposing two different dates, the gap since any decision and the gap since this one,
        // which reads as the game contradicting itself: "a year later" above "in year 83".
        return ord
    }

    /**
     * THE BODY HAS TO KNOW TOO.
     *
     * The header counter was welcomed by every reviewer who saw it and fixed nothing on its own,
     * because underneath it sat a body written as though this had never happened: a "permanent fix"
     * re-sold after it had visibly failed, two expeditions to a formation whose fiction still said
     * nobody had been, a counter reading "the 7th time" over a body saying "a third water
     * emergency". One reviewer, having found the single event in the corpus that does acknowledge
     * its own repeat: *"D86 is the one success and proves the fix is two clauses of text."*
     *
     * Authored variant text is better and there is one event's worth of it in 105. This is the
     * floor under all the others: composed from what the engine already knows, so it is true of the
     * thousandth event on the day it is written, and it says the one thing the player most wants
     * acknowledged — that this is not the first time, and what they did last time did not settle it.
     */
    private fun repeatPreamble(e: GameEvent, s: WorldState): String {
        val n = seenBefore[e.id] ?: 0
        if (n == 0) return ""
        val gap = s.year - (lastYearOn[e.id] ?: s.year)
        val what = lastChoiceOn[e.id]
        val when_ = when { gap <= 1 -> "Last year"; gap <= 3 -> "Only $gap years ago"
                           else -> "$gap years ago" }
        // The long-gap clause is an APPOSITIVE, not part of the time phrase. Welded into `when_`
        // it produced "25 years ago, long enough that most of them were not born you faced this",
        // which two readers in two separate runs quoted back as broken (playtests 05 D24, 06 D24).
        val since = if (gap >= 25) ", long enough that most of the people here now were not born then," else ""
        val ans = if (what == null) "you settled this" else "you answered it with “$what”"
        val again = if (n >= 3) "It has come back every time since."
                    else "It did not stay settled."
        return "$when_$since you faced this, and $ans. $again"
    }

    override fun onPresent(s: WorldState, e: GameEvent, title: String, body: String,
                           all: List<Option>, offered: List<Option>, caps: Set<String>) {
        pendingId = e.id
        pendingOffered = offered
        lastSeverity = severityOf(e)
        lastTitle = title
        val gap = gapWords(s.month)
        lastShownMonth = s.month
        pending = DecisionView(
            title = title, severity = lastSeverity, year = s.year,
            monthOfYear = s.month % 12 + 1, sinceLast = gap, theme = e.theme(),
            debugId = e.id,
            debugWhy = "source=" + e.source + "  kind=" + e.kind +
                    (if (e.pressures.isEmpty()) "" else "  expresses=" +
                        e.pressures.entries.joinToString(",") { it.key + "x" + it.value }) +
                    "  weight=" + e.weight + "  cooldown=" + e.cooldownYears + "y",
            debugSelection = engine.dir.lastWhy,
            timesBefore = seenBefore[e.id] ?: 0,
            previously = previously(e),
            stakes = stakesOf(s),
            body = listOf(repeatPreamble(e, s), body).filter { it.isNotEmpty() }.joinToString("\n\n"),
            options = displayable(s, all, offered, caps).map { (o, display) ->
                // A LOCKED option's gate line already says what the store holds ("spare fuel is 0;
                // this needs 2"), so repeating it on the price line said the same number twice.
                val avail = offered.contains(o)
                // A BRANCH THE SHIP DECIDES IS NOT A GAMBLE. "Hold in orbit and survey" has three
                // outcomes gated on the instruments aboard, so on any one ship exactly one of them
                // can happen, and it was shown as "🎲 gamble" with none of its price: five red
                // chips against nothing, on the first decision of the game. The engine draws only
                // from the live branches (Sim.resolve); the screen now reads the same list.
                val live = Physics.liveOutcomes(s, o, caps)
                val (pays, gains) = priceOf(s, o, showHave = avail, effects =
                    if (live.size == 1) mergeEffects(o.effects + live[0].effects) else o.effects)
                val chips = lastChips.toList()
                val odds = if (live.size > 1) oddsOf(s, live) else emptyList()
                OptionView(
                    number = numberOf[o] ?: 0, label = o.label, hint = o.hint,
                    chips = chips, lethal = chips.any { it.kind == "lives" },
                    available = avail,
                    // A LOCK MUST NAME SOMETHING THE PLAYER COULD GO AND GET. "you cannot hold a
                    // formal vote" at a colony of 23 adults, and "you cannot keep an audited
                    // account" at one that smelts metal and reaches orbit, read as the game not
                    // knowing its own state — because these verbs come from INSTITUTIONS and
                    // MODULES, not from the crew being incapable. Readers in all four runs said
                    // the same thing: the string maps to nothing in CAN DO and nothing in the
                    // manifest, so there is no way to tell whether it is ever acquirable.
                    needs = capsOf(o).filter { !caps.contains(it) }.map { c ->
                        val from = Content.corpus.modules.filter { it.grants.contains(c) }
                            .map { it.name.lowercase() }
                        if (from.isEmpty()) Content.capShort(c)
                        else Content.capShort(c) + " \u2014 that takes " + from.take(2).joinToString(" or ")
                    },
                    whyNot = whyNot(s, o, caps),
                    debug = explain(o),
                    tags = o.tags,
                    starts = startsAmbition(o),
                    learns = learnsFrom(s, o, live),
                    pays = pays, gains = gains,
                    uncertain = live.size > 1, odds = odds,
                    priceNote = if (pays.isEmpty() && gains.isEmpty() && o.outcomes.isEmpty())
                        "costs nothing you can measure" else "",
                    takenBefore = lastChoiceOn[e.id] == o.label,
                    display = display
                )
            }
        )
    }

    /**
     * WHICH OPTIONS REACH THE SCREEN, AND HOW — and the numbering that follows from it.
     *
     * A locked option is one of three things and the engine cannot tell them apart, so the author
     * says (`Option.whenLocked`, see [WhenLocked]). What is left to work out here is the AUTO case
     * and the numbers.
     *
     * AUTO reads what is actually missing. If every unmet gate is a capability, the lock is a fact
     * about the ship that left Earth and cannot change this century — worth a name and no more.
     * Anything else (fuel, a flag, a state of the colony) is a fact about right now, and right now
     * is what the player is being asked to read.
     *
     * Numbering runs over the FULL ones only, so the player never sees a gap where a hidden option
     * used to be. [numberOf] is how [choose] gets from a tapped number back to an Option, which
     * used to be `all[n - 1]` and cannot be any more.
     */
    private fun displayable(s: WorldState, all: List<Option>, offered: List<Option>,
                            caps: Set<String>): List<Pair<Option, OptionDisplay>> {
        numberOf.clear()
        val out = mutableListOf<Pair<Option, OptionDisplay>>()
        for (o in all) {
            if (offered.contains(o)) { out.add(o to OptionDisplay.FULL); continue }
            val how = when (o.lockedAs) {
                WhenLocked.SHOW -> OptionDisplay.FULL
                WhenLocked.NAME -> OptionDisplay.NAME
                WhenLocked.HIDE -> null
                WhenLocked.AUTO -> {
                    val unmet = o.requires.filter { !Expr.cond(s, caps, it) }
                    val terms = { pick: (String) -> Boolean ->
                        unmet.isNotEmpty() && unmet.all { r -> r.split('|').all { pick(it.trim()) } }
                    }
                    // A POSITIVE FLAG GATE IS A ROAD NOT TAKEN, AND THE LABEL SAYS SO. Eighteen
                    // options in the corpus are callbacks to an earlier decision — "Offer them the
                    // same terms you offered last time", "Use the words you already say over the
                    // dead", "Keep the date you named" — and their labels only parse if the thing
                    // happened. Locked, they described a past the player does not have, in the one
                    // place they can do nothing about it. `!flag:` is the opposite case: the flag
                    // IS set and is blocking, which is a fact about right now, and stays visible.
                    val counterfactual = unmet.any { r ->
                        r.split('|').all { it.trim().startsWith("flag:") }
                    }
                    when {
                        counterfactual -> null
                        // Equipment. Nothing that happens this century changes what left Earth.
                        terms { it.trimStart('!').startsWith("cap:") } -> OptionDisplay.NAME
                        // "ONLY WHEN THINGS ARE BAD" — the direction of the comparison says which.
                        // `res.deltaV >= 2` is a shortage and the player must see it: the ordinary
                        // way down is shut and this is why. `sim.powerRatio <= 1.2` is the opposite
                        // — the option exists to answer a scarcity that is not happening — and a
                        // playtester hit exactly that: *"it is awkward that 'power first' is not
                        // available when there IS enough power."* There is nothing to tell them.
                        terms { SCARCITY_GATE.matches(it) } -> null
                        else -> OptionDisplay.FULL
                    }
                }
            } ?: continue
            out.add(o to how)
        }
        var n = 0
        for ((o, how) in out) if (how == OptionDisplay.FULL) numberOf[o] = ++n
        // Named ones last, so the numbers on screen run 1..n without a name interrupting them.
        return out.sortedBy { it.second == OptionDisplay.NAME }
    }

    /** Tapped number -> the Option it stands for, for exactly the decision now on screen. */
    private val numberOf = mutableMapOf<Option, Int>()

    /** `res.food <= 400`, `sim.powerRatio < 1.2` — a gate that only opens when something is short. */
    private val SCARCITY_GATE = Regex("""^[A-Za-z_][A-Za-z0-9_.]*\s*<=?\s*-?[0-9.]+$""")

    /** Bot-driven runs never call [choose], so record from the trace instead. */
    private fun recordFromTrace() {
        val t = state.trace.lastOrNull() ?: return
        if (t.kind == "SYMPTOM" || t.chosen == "-") return
        if (log.lastOrNull()?.let { it.year == t.month / 12 && it.title == t.title } == true) return
        log.add(HistoryEntry(t.month / 12, t.title, t.chosen, lastSeverity))
    }

    /** In a watched run the bot answers, so the decision still has to be shown to the reader. */
    private fun renderObserved() {
        if (!watching) return
        val d = pending ?: return
        val t = state.trace.lastOrNull() ?: return
        if (t.chosen == "-") return
        pending = null
        ui.observed(d, t.chosen)
    }

    /**
     * The gamble's result WITH what it cost. "Half of it has to be re-founded on rock" was the
     * whole report of the blind landing going badly; the thirty spares it took, and the morale,
     * were never mentioned. The branch is priced exactly as it was on the card before the roll.
     */
    override fun onOutcome(text: String, effects: List<String>) {
        priceOf(state, Option(label = ""), showHave = false, effects = effects)
        val price = lastChips.sortedBy { if (it.good) 1 else 0 }.joinToString(" · ") { c ->
            (if (c.kind == "lives") "☠ " else if (c.good) "✚ " else "✖ ") + c.text +
                    (if (c.dots > 0) " " + "●".repeat(c.dots) else "")
        }
        ui.outcome(if (price.isEmpty()) text else "$text  $price")
    }

    override fun onResolved() { renderObserved(); recordFromTrace() }

    override fun onSymptom(title: String, body: String) { notes.add("$title — $body") }

    /**
     * "10 people died · 11 people died · 9 people died" was one year's report, a line a month,
     * never totalled and never given a cause. One line, the total, and the cause when the larder
     * was empty.
     */
    private fun mergeDeaths(s: WorldState, lines: List<String>): List<String> {
        val rx = Regex("^([0-9]+) people died$")
        var total = 0; var first = -1
        val out = mutableListOf<String>()
        for (l in lines) {
            val n = if (l == "somebody died") 1 else rx.find(l)?.groupValues?.get(1)?.toIntOrNull()
            if (n == null) { out += l; continue }
            if (first < 0) { first = out.size; out += "" }
            total += n
        }
        if (first >= 0) out[first] = (if (total == 1) "somebody died this year" else "$total people died this year") +
                (if (s.starvingMonths > 0 || s.food < 1.0) ", most of them of hunger" else "")
        // A gauge that crossed three bands in one year read as three contradicting lines
        // ("morale is steady now, not willing" ... "morale is mutinous now, not steady"). One net
        // line per gauge, from where the year started to where it ended; nothing if it came back.
        val band = Regex("^([a-z]+) is ([a-z]+) now, not ([a-z]+)$")
        val from = mutableMapOf<String, String>(); val to = mutableMapOf<String, String>()
        val slot = mutableMapOf<String, Int>()
        val merged = mutableListOf<String>()
        for (l in out) {
            val m = band.find(l)
            if (m == null) { if (l !in merged) merged += l; continue }
            val (g, now, was) = m.destructured
            if (g !in from) from[g] = was; to[g] = now
            if (g !in slot) { slot[g] = merged.size; merged += "" }
        }
        for ((g, i) in slot) merged[i] = if (from[g] == to[g]) "" else "$g is ${to[g]} now, not ${from[g]}"
        return merged.filter { it.isNotEmpty() }
    }

    override fun onYearEnd(s: WorldState, decisionsThisYear: Int) {
        s.pendingAmbition?.let { ui.outcome(it.done); s.pendingAmbition = null }
        val absorbed = s.absorbed - lastAbsorbed
        lastAbsorbed = s.absorbed
        recordFromTrace()
        // Everything the simulation did while nobody was being asked. Ten consecutive years of
        // falling food went by in one playtest logged as "nothing demanded your attention", and
        // forty-two deaths were reported as the line "- year 54".
        val changed = mergeDeaths(s, s.changes.toList()); s.changes.clear()
        if (decisionsThisYear == 0 && notes.isEmpty() && changed.isEmpty() && absorbed == 0) return
        ui.yearReport(YearReportView(s.year, decisionsThisYear, changed + notes.toList(), absorbed))
        notes.clear()
    }

    override fun onEnd(s: WorldState, reason: String) { notes.clear() }

    // ------------------------------------------------------------------ SessionInfo
    /**
     * The lock sentence this option would show right now. Exposed so the regression suite can walk
     * every option of every event and assert that nothing the interface prints carries a token
     * from the expression language — four raw flag names reached players before it did.
     */
    fun lockReasonFor(o: Option): String {
        val caps = Physics.capabilities(state)
        val needs = capsOf(o).filter { !caps.contains(it) }.map { Content.capShort(it) }
        return (if (needs.isNotEmpty()) "needs " + needs.joinToString(" or ") + " " else "") +
                whyNot(state, o, caps)
    }

    /** The decision currently on screen, for the tests that assert what a player would read. */
    fun pendingDecision(): DecisionView? = pending

    /** Whether [flagWords] has a sentence for this flag. For the test that keeps the list honest. */
    fun hasWordsForFlag(name: String) = flagWords.containsKey(name)

    override fun colony(): ColonyView = colonyView(state)
    override fun modules(): List<ModuleView> = moduleViews(state)
    override fun budgets(): List<BudgetView> = budgetViews(state)
    override fun crewNote(): List<String> = crewNotes(state)
    override fun history(): List<HistoryEntry> = log.toList()
    override fun planetName(): String = state.planet.name

    /**
     * A world has to be good for something, or the whole game is triage. Only reported once the
     * relevant parameter is actually known — noticing that the ridges are full of ore is the seed
     * of a constructive goal, and it should be something the player discovers rather than is told.
     */
    override fun planetPotential(): List<String> {
        val known = Knowledge.facts(state).filter { it.confidence != Confidence.UNKNOWN }
            .map { it.key }.toSet()
        val needs = mapOf("iron_economy" to "ore", "deep_water" to "water",
                          "geothermal" to "seismic", "bio_industry" to "flora")
        return Physics.localAdvantages(state.planet)
            .filter { known.contains(needs[it.first]) }
            .map { it.second }
    }

    /**
     * What the colony is working toward, derived from state rather than authored.
     *
     * Derived on purpose: an authored objective list would be another thing to write 1000 times and
     * would go stale the moment the simulation changed. These read the same numbers the player can
     * see, so a goal can never claim something the status panel contradicts.
     *
     * One long-term aim and three near-term ones. The long-term aim changes with the phase, because
     * "reach the planet" and "hold this place for a century" are genuinely different games.
     */
    /**
     * Deliberately short. The designer's correction: goals should be *implied and player-driven*,
     * living in the player's head rather than enumerated. "Low food" already implies its own goal
     * if the player can see food falling; spelling it out as an objective with a progress bar turns
     * a colony into a chore list and does the interesting thinking for them.
     *
     * So this returns only two kinds of thing:
     *   - the ONE direct aim (the mission), which the designer allows to be explicit;
     *   - whatever the player has CHOSEN to build, because a commitment you made deserves a bar.
     *
     * Everything else - food, parts, people, the ore in that ridge - is communicated as state and
     * *trend*, and the goal it implies is left to the player to form.
     */
    override fun goals(): List<GoalView> {
        val s = state
        val out = mutableListOf<GoalView>()

        // ---- the one direct aim ----
        if (s.phase == Phase.SURFACE) {
            out.add(GoalView("Hold this place for a hundred years",
                (s.year / 100.0).coerceIn(0.0, 1.0),
                "year " + s.year + " of 100", longTerm = true))
        } else if (s.hasFlag("arrived")) {
            out.add(GoalView("Commit to a world", 0.9,
                "you are in orbit and have not decided", longTerm = true))
        } else {
            val need = max(1, s.build.arrivalYear)
            out.add(GoalView("Reach " + s.planet.name,
                (s.year.toDouble() / need).coerceIn(0.0, 1.0),
                "year " + s.year + " of " + need, longTerm = true))
        }

        // ---- and whatever the colony chose to build ----
        for (a in s.ambitions) {
            val def = AMBITIONS.first { it.id == a.id }
            val total = def.years * 12
            out.add(GoalView(def.title, a.monthsDone.toDouble() / total,
                "" + (a.monthsDone / 12) + " of " + def.years + " years' work",
                longTerm = false,
                blocked = if (Physics.powerRatio(s) < 0.85) "no power to spare"
                          else if (s.spares < def.sparesPerYear / 12) "no parts to spare"
                          else if (Physics.labourIndex(s) <= 0.85) "nobody has the hours"
                          else ""))
        }
        return out
    }

    override fun planetFacts(): List<PlanetFactView> = Knowledge.facts(state).map { f ->
        val text = when {
            f.value == null -> "unknown"
            // "0.94 ± 0.03 x Earth", not "0.94 x Earth ± 0.03 x Earth"
            f.confidence == Confidence.ESTIMATED ->
                fmtVal(f.value, "") + " \u00b1 " + fmt(f.margin) + " " + f.unit
            else -> fmtVal(f.value, f.unit)
        }
        PlanetFactView(f.label, f.shortLabel, f.confidence.name.lowercase(), text,
            if (f.value == null) "?" else fmtVal(f.value, f.unit), f.learnedBy, f.meaning)
    }

    private fun fmtVal(v: Double, unit: String): String {
        val n = if (unit == "%" || unit == "\u00b0C" || unit == "h") "" + v.roundToInt() else fmt(v)
        return if (unit.isEmpty()) n else n + " " + unit
    }

    // ------------------------------------------------------------------ translation
    private fun GameEvent.theme(): String = if (pressures.isEmpty()) "" else pressures.keys.first()

    /**
     * THE TAG DESCRIBES THE COLONY, NOT THE EVENT'S GENRE.
     *
     * `[ CRISIS ]` was a fixed property of the event, so it fired on its own schedule regardless of
     * what the numbers above it said. Readers caught it repeatedly: a dust storm tagged CRISIS four
     * times at a colony whose fission reactor covered its entire draw; a medicine crisis with twelve
     * years of stock while food read "gone within the year"; a water crisis in the year power fell
     * by two thirds. *"The loudest thing on this whole screen is the unlanded ship burning its last
     * water over an ice field, and the game is asking me about tray shifts instead."*
     *
     * A crisis event at a comfortable colony is still a problem worth a decision — it is just not a
     * crisis, and calling it one teaches the player that the word is noise. So the word is earned:
     * something the player can SEE has to be wrong. This does not excuse the underlying gating
     * problem (an event should not assert a scarcity it does not require), which is an authoring
     * pass; it stops the interface from amplifying it.
     */
    private fun severityOf(e: GameEvent): Severity = when (e.kindE) {
        EventKind.OPPORTUNITY -> Severity.OPPORTUNITY
        EventKind.CRISIS -> if (inTrouble(state)) Severity.CRISIS else Severity.NOTABLE
        else -> Severity.NOTABLE
    }

    /** Is anything the player can see actually wrong right now? */
    private fun inTrouble(s: WorldState): Boolean {
        val c = colonyView(s)
        return c.stocks.any { it.low } || c.powerShort || c.broken.isNotEmpty() ||
                c.gauges.any { it.band == 0 } || Physics.labourIndex(s) < 0.85
    }

    /**
     * What an option would teach the colony, if anything. Only `planet_scanned` changes what is
     * knowable today, so that is the only flag consulted — but the shape generalises: an option
     * that moves the knowledge table has to say what it moves, because the answer is build-specific
     * and a written hint cannot know it.
     */
    private fun learnsFrom(s: WorldState, o: Option, live: List<Outcome>): String {
        val effs = o.effects + live.flatMap { it.effects }
        fun sets(flag: String) = effs.any { it.trim().startsWith("flag:$flag") && it.contains("true") }
        if (!sets("planet_scanned")) return ""
        val gained = Knowledge.wouldLearn(s)
        return if (gained.isEmpty()) "nothing your instruments can reach from here"
               else gained.joinToString(", ")
    }

    /** "gauge.morale -= 8" and "gauge.morale -= 3" priced as one chip, not two. */
    private fun mergeEffects(effs: List<String>): List<String> {
        val num = Regex("""^((?:res|gauge|pressure|pop)\.[A-Za-z]+)\s*(\+=|-=)\s*(-?[0-9.]+)$""")
        val sums = LinkedHashMap<String, Double>(); val out = mutableListOf<String>()
        for (raw in effs) {
            val m = num.find(raw.trim())
            if (m == null) { out.add(raw); continue }
            val (k, op, v) = m.destructured
            if (k !in sums) out.add("\u0000$k")
            sums[k] = (sums[k] ?: 0.0) + v.toDouble() * (if (op == "-=") -1 else 1)
        }
        return out.mapNotNull { e ->
            if (!e.startsWith("\u0000")) e
            else { val k = e.substring(1); val d = sums[k]!!
                   if (d == 0.0) null else "$k " + (if (d < 0) "-= " else "+= ") + abs(d).toString() }
        }
    }

    /** The long undertaking an option begins, if any, named and priced in years. */
    private fun startsAmbition(o: Option): String {
        val id = (o.effects + o.outcomes.flatMap { it.effects })
            .firstOrNull { it.trim().startsWith("ambition:") }
            ?.trim()?.removePrefix("ambition:")?.trim() ?: return ""
        val a = AMBITIONS.firstOrNull { it.id == id } ?: return ""
        return a.title + " \u2014 " + a.years + " years"
    }

    private fun capsOf(o: Option): List<String> = o.requires
        .flatMap { it.split('|') }.map { it.trim() }
        .filter { it.startsWith("cap:") }.map { it.removePrefix("cap:") }

    private fun capLabel(id: String) = id.lowercase().replace('_', ' ')

    private fun capVerb(id: String) =
        Content.corpus.capabilities.firstOrNull { it.id == id }?.verb?.substringBefore(" (") ?: capLabel(id)

    /**
     * Why an option is locked, in plain language.
     *
     * This used to consider only capabilities: any non-`cap:` term counted as satisfied, so an
     * option locked by `res.deltaV >= 20` reported nothing at all and the player was shown a
     * greyed line with no reason. The MVP's second stated purpose is that unavailable choices
     * TEACH, and a silent lock teaches nothing. Now every unmet term gets a sentence.
     */
    private fun whyNot(s: WorldState, o: Option, caps: Set<String>): String {
        val unmet = o.requires.filter { !Expr.cond(s, caps, it) }
        if (unmet.isEmpty()) return ""
        return unmet.joinToString("; ") { r ->
            r.split('|').joinToString(" or ") { alt ->
                val a = alt.trim()
                if (a.startsWith("cap:")) "no way to " + capVerb(a.removePrefix("cap:"))
                else plainGate(s, a)
            }.ifBlank { "not possible here" }
        }
    }

    /** `res.deltaV >= 20` -> "needs spare fuel of 20 — you have 8". */
    private fun plainGate(s: WorldState, term: String): String {
        // A FLAG IS A PAST DECISION, and its lock has to say so. `LOCKED - the colony does not meet
        // flag:council_granted` was called "the single worst line on the screen"; four raw flag
        // names reached players across two playtests. The reviewers' own fix generalises to every
        // institutional lock: *"the real reason should be political, not technical — 'you never
        // established a franchise'. That version is both true and stings, because it makes the lock
        // MY past decision instead of a missing crate of hardware."*
        Regex("""^(!?)flag:([A-Za-z0-9_]+)$""").find(term.trim())?.let {
            val (neg, name) = it.destructured
            val said = flagWords[name] ?: name.replace('_', ' ')
            return if (neg.isEmpty()) "not unless $said" else "not while $said"
        }
        Regex("""^phase\s*(==|!=)\s*([A-Z]+)$""").find(term.trim())?.let {
            val (op, ph) = it.destructured
            val where = when (ph) { "SURFACE" -> "on the ground"; "ORBIT" -> "in orbit"; else -> "under way" }
            return if (op == "==") "only $where" else "not while $where"
        }
        val m = Regex("""^(!?)([A-Za-z_][A-Za-z0-9_.]*)\s*(<=|>=|==|!=|<|>)\s*(-?[0-9.]+)$""")
            .find(term) ?: return ""
        val (neg, path, op, rhs) = m.destructured
        val name = gateNames[path] ?: path
        val have = Expr.value(s, path)
        val n = { v: Double -> if (v >= 10 || v == v.toInt().toDouble()) Fmt.int(v) else Fmt.dec(v, 2) }
        val want = rhs.toDoubleOrNull() ?: return ""
        val youHave = have?.let { " \u2014 you have ${n(it)}" } ?: ""
        return when {
            neg.isNotEmpty() -> "not while $name"
            // "needs spare fuel of 2 — you have 0" put the quantity in a possessive slot it does
            // not fit ("X of N") and buried the player's own number behind a dash. Lead with the
            // state, then the threshold.
            op.startsWith(">") -> have?.let { "$name is ${n(it)}; this needs ${n(want)}" }
                ?: "needs $name of ${n(want)}"
            // An UPPER-bound gate is not permission being withheld: it means the thing this option
            // fixes is not broken. "Only while spare power is under 1.20 — you have 2.51" reads as
            // an arbitrary restriction; "power is comfortable, so this is not the problem" is the
            // same fact stated as information the player can act on.
            // No threshold. "(5246, and this needs it under 400)" read to one player as *"a debug
            // string that leaked past the narrative layer"* — and it is: the number is a dial, not
            // a fact about the colony.
            op.startsWith("<") -> "$name is not the problem here"
            else -> "needs $name to be exactly ${n(want)}$youHave"
        }
    }

    /**
     * A FLAG IS A PAST DECISION. Every one of these reads as a clause the player can finish:
     * "not unless <you have given them an elected council>". A flag with no entry here falls back
     * to its own name with the underscores taken out, which is ugly but is still English — the
     * failure mode being fixed is `flag:council_granted` reaching a player verbatim.
     *
     * Enforced: `Content.validate()` fails the build on a flag the corpus reads but nobody names,
     * so this list cannot fall behind the corpus the way the capability labels did.
     */
    private val flagWords = mapOf(
        "ai_triage" to "you have let the machine decide who is treated",
        "amnesty_given" to "you have pardoned them",
        "arrived" to "the ship has reached its target",
        "authority_broken" to "your authority has been broken once already",
        "births_forbidden" to "births are forbidden",
        "births_rationed" to "births are rationed",
        "births_screened" to "births are screened",
        "burial_rite" to "the colony has a rite for its dead",
        "council_granted" to "you have given them an elected council",
        "declared_independent" to "you have declared independence from Earth",
        "doctrine_endorsed" to "you have blessed a doctrine",
        "earth_silent" to "Earth has gone quiet",
        "first_night_done" to "the colony has spent a night on the ground",
        "force_used" to "you have used force on your own people",
        "handover" to "you have handed over authority",
        "landed" to "the colony has landed",
        "landing_promised" to "you promised them a landing",
        "law_written" to "the colony has a written law",
        "mutiny_crushed" to "you have put down a mutiny",
        "no_return" to "the ship can never leave again",
        "open_records" to "the records are open to everyone",
        "orbit_retained" to "you kept a crew in orbit",
        "planet_scanned" to "the world has been surveyed properly",
        "priorities_set" to "you have set the colony's priorities in writing",
        "schism_taken_sides" to "you took a side in the schism",
        "sensor_limits" to "you have limited what the sensors may look at",
        "settled" to "the colony is settled",
        "triage_settled" to "you have already settled who decides who is treated",
        "infirmary_staffed" to "the infirmary already has people permanently on it",
        "off_the_fault" to "you moved the settlement off the fault line",
        "ship_stripped" to "the ship has been stripped for parts",
        "turning_back" to "the colony has decided to turn back",
        "watched_them" to "you have had them watched",
        "withheld_news" to "you kept news from them")

    /** The quantities a locked option can name, in the words the interface already uses. */
    private val gateNames = mapOf(
        "res.food" to "food", "res.water" to "water", "res.spares" to "spares",
        "res.medicine" to "medicine", "res.deltaV" to "spare fuel", "res.energy" to "power",
        "res.cultivated" to "growing area", "res.data" to "knowledge",
        "pop.total" to "people", "pop.adults" to "adults", "pop.children" to "children",
        "pop.elders" to "elders", "pop.cryo" to "sleepers in storage",
        "year" to "years since launch", "sim.settledYears" to "years on the ground",
        "sim.deaths" to "deaths", "sim.deathsNotable" to "deaths in the colony's memory",
        "sim.bornHere" to "people born here", "sim.powerRatio" to "spare power",
        "sim.fed" to "food margin", "sim.broken" to "broken systems",
        "gauge.competence" to "skill", "gauge.morale" to "morale", "gauge.health" to "health",
        "gauge.cohesion" to "unity", "gauge.legitimacy" to "standing"
    )

    // The ladder itself lives in Model.kt — the change ledger reports crossings on it too.
    private val bands = GAUGE_BANDS
    private val gaugeLabels = GAUGE_NAMES

    /** How bad a strain is, on the same five rungs the moods use. */
    private val STRAIN_RUNGS = listOf("slight", "noticeable", "serious", "bad", "dangerous")

    /** What a pressure IS, as a thing the player can be told the level of. */
    private val pressureNouns = mapOf(
        "dissent" to "resentment",
        "inequality" to "unfairness",
        "secrecy" to "secrets",
        "faith" to "turning to faith",
        "aiAutonomy" to "what is left to the decision core",
        "earthEstrangement" to "distance from Earth's ways",
        "entropy" to "wear on the machines",
        "ecoStrain" to "strain on air, water and crops",
        "xenoExposure" to "exposure to the planet",
        "founderDrift" to "drift from the founders' plan"
    )

    /**
     * What a pressure MOVING means, in words. Never its 0..1 value -- the state stays hidden and
     * is felt through symptom events; only the direction of a change the player is being asked to
     * buy reaches the price line. Both halves are comparatives so "a good deal " can prefix either.
     * first = the pressure FELL (good), second = it ROSE (bad).
     */
    private val pressureWords = mapOf(
        // PLAIN WORDS A FIRST-TIME PLAYER CAN READ WITHOUT THE DESIGN DOCS (playtest 2026-09-25).
        // "more drift from the mission as written", "more taken on faith" and "more decided by the
        // core alone" were on nearly every screen and meant nothing to someone who had not read
        // how the pressures work. Each now names the thing a colonist would notice.
        "dissent" to Pair("less resentment", "more resentment"),
        "inequality" to Pair("fairer", "less fair"),
        "secrecy" to Pair("fewer secrets", "more secrets"),
        "faith" to Pair("less turning to faith", "more turning to faith"),
        "aiAutonomy" to Pair("less left to the decision core", "more left to the decision core"),
        "earthEstrangement" to Pair("closer ties to Earth", "weaker ties to Earth"),
        "entropy" to Pair("less wear on the machines", "more wear on the machines"),
        "ecoStrain" to Pair("less strain on air, water and crops", "more strain on air, water and crops"),
        "xenoExposure" to Pair("less exposure to the planet", "more exposure to the planet"),
        "founderDrift" to Pair("closer to the founders' plan", "further from the founders' plan")
    )

    /**
     * Turns the effect mini-language into something readable. Designer-facing: it is the answer to
     * "what did that actually do?", which is otherwise only visible by reading the corpus source.
     */
    private fun explain(o: Option): List<String> {
        val out = mutableListOf<String>()
        fun line(effs: List<String>, prefix: String = "") {
            val parts = effs.mapNotNull { readEffect(it) }
            if (parts.isNotEmpty()) out.add(prefix + parts.joinToString(" · "))
        }
        // An ambition gets its own line: its rewards are a list of effects too, so blending them
        // with the option's immediate effects reads as one undifferentiated run of numbers.
        val (amb, now) = o.effects.partition { it.trim().startsWith("ambition:") }
        line(now)
        line(amb)
        for (oc in o.outcomes) {
            val pct = (oc.p * 100).roundToInt()
            line(oc.effects, "$pct% ")
            if (oc.effects.isEmpty()) out.add("$pct% (nothing)")
        }
        if (o.tags.isNotEmpty()) out.add("identity: " + o.tags.joinToString(", "))
        return out
    }

    // ------------------------------------------------------------------ what an option costs
    /**
     * THE PRICE, IN THE UNITS THE STATUS PANEL ALREADY USES — derived from the effects, never written.
     *
     * The single most-repeated finding of two full playtests, from 126 independent readers: the
     * status block is scrupulous (`spares 429 [86 yr left]`, `one ascent needs 2`) and the options
     * are priced entirely in prose — "cheaper", "months of work", "the most expensive one". Not one
     * number in 126 decisions. So readers took whichever option happened to name a quantity, and
     * said so: *"I'm choosing between four unquantified outcomes and one quantified one. Naturally
     * I take the quantified one."* An option with no stated cost became dominant by default.
     *
     * Computing it rather than authoring it is the whole point, three times over:
     *  - it cannot lie, and it cannot drift out of date, which "an option that lies" was;
     *  - it costs no authoring, so it is already true of the thousandth event as of the first;
     *  - an option that promises to build a second shelter and changes nothing now shows an EMPTY
     *    price, which is exactly the defect made visible instead of hidden.
     *
     * The hidden layer stays hidden (`00-VISION.md` §4): pressures and axes never appear here, and
     * a gauge is named without a number because the player sees gauges as words, not percentages.
     */
    /**
     * AN OPTION MAY NOT BILL A STOCK THE PANEL SAYS IS NOT THERE, SILENTLY.
     *
     * `offeredOptions` filters on `requires` alone, so affordability is enforced only where an
     * author happened to write it. Fuel is always gated; spares, food and medicine never were. The
     * 2026-08-31 run hit this on six of fourteen late screens, and D68 is the proof: the SAME
     * screen locked two options with "needs spare fuel of 12 - you have 0" while offering a
     * 45-spares option against a stock of 29. Every reader drew the same conclusion -- "either the
     * readout is wrong or the availability check is".
     *
     * Filtering the option out is the wrong fix: `res` effects clamp at zero, and a famine in
     * which every food-costing option disappears leaves a crisis with nothing to answer it. What
     * was actually broken is the PRICE: because the spend clamps, a 45-spares option at 29 spares
     * costs 29, so the expensive-sounding option was genuinely cheaper than the modest one -- which
     * is the mechanism behind "the expensive option is cheaper and gains more", reported on 11 of
     * 14 screens. The line now says what will really be spent, and the option stays takeable.
     */
    private fun priceOf(s: WorldState, o: Option, showHave: Boolean = true,
                        effects: List<String> = o.effects): Pair<List<String>, List<String>> {
        val pays = mutableListOf<String>(); val gains = mutableListOf<String>()
        lastChips.clear()
        fun put(neg: Boolean, text: String) = (if (neg) pays else gains).add(text)
        fun chip(neg: Boolean, text: String, dots: Int = 0, kind: String = "") {
            lastChips.add(Chip(text, good = !neg, dots = dots, kind = kind))
        }
        val people = max(1.0, s.popTotal)
        for (raw in effects) {
            val e = raw.trim()
            Regex("""^res\.energy\s*(\+=|-=)\s*(-?[0-9.]+)$""").find(e)?.let {
                val (op, v) = it.destructured
                val d = v.toDouble() * (if (op == "-=") -1 else 1)
                put(d < 0, "${fmtQty(abs(d))} kW " + (if (d < 0) "of power" else "of power freed"))
                chip(d < 0, "power ${fmtQty(abs(d))} kW", kind = "power")
                return@let
            } ?: Regex("""^res\.([A-Za-z]+)\s*(\+=|-=)\s*(-?[0-9.]+)$""").find(e)?.let {
                val (k, op, v) = it.destructured
                val d = v.toDouble() * (if (op == "-=") -1 else 1)
                val unit = resUnits[k] ?: k
                // THE STOCK COUNTS MOVED HERE FROM THE STATUS STRIP. "water 181" cannot be
                // read, because these quantities have no unit — 181 of what? — so the strip shows
                // only the gloss ("8 months of drinking if the loop stops"), which is the half that
                // means something. What the number was really for is affordability, and that
                // belongs on the option spending it: any bite worth more than half the store says
                // what the store holds.
                val have = Expr.value(s, "res.$k")
                val tight = showHave && d < 0 && have != null && have < abs(d) * 2
                if (k == "food" || k == "water") {
                    // FOOD IS PERSON-MONTHS, and "25 food" means nothing to a reader: at 169 people
                    // it is four days, at 20 it is five weeks. Say the time it buys at today's
                    // headcount, the same unit the stores line uses.
                    val t = supplyTime(abs(d) / people)
                    put(d < 0, "$t of $k" + (if (tight) " (you have ${supplyTime(have!! / people)})" else ""))
                    chip(d < 0, "$k $t", kind = "stock")
                } else {
                    put(d < 0, fmtQty(abs(d)) + " " + unit +
                            (if (tight) " (you have " + fmtQty(have!!) + ")" else ""))
                    chip(d < 0, "$unit ${fmtQty(abs(d))}", kind = "stock")
                }
            } ?: Regex("""^pop\.([A-Za-z]+)\s*(\+=|-=)\s*(-?[0-9.]+)$""").find(e)?.let {
                val (k, op, v) = it.destructured
                val d = v.toDouble() * (if (op == "-=") -1 else 1)
                put(d < 0, fmtQty(abs(d)) + " " + k)
                chip(d < 0, fmtQty(abs(d)) + " " + k, kind = "people")
            } ?: Regex("""^gauge\.([A-Za-z]+)\s*(\+=|-=)\s*(-?[0-9.]+)$""").find(e)?.let {
                val (k, op, v) = it.destructured
                val d = v.toDouble() * (if (op == "-=") -1 else 1)
                // Named, never numbered: the player reads morale as "willing", not as 43.
                // "skill, for a year or two" on the GAINS line was read back as a question: what
                // does the word "skill" mean here? A bare noun states a quantity and no direction,
                // and the reader has to infer both from which emoji it sat behind. So the line says
                // which way it moves, and the colony readout says what the word is a word about.
                val size = if (abs(d) >= 8) "a good deal " else ""
                val way = if (d < 0) "less " else "more "
                // THE SINGLE MOST-REPORTED FINDING OF ITERATION 2: twenty of twenty-three reviewers
                // in one run, unprompted. Morale and skill sat at 5/5 for the whole late game while
                // options kept selling them, and on four screens EVERY gain offered was already at
                // its ceiling. Round 1 gave the reader the ladder position; offering them something
                // above the top of that ladder is the same lie one step further on.
                //
                // And gauges are `chase`d toward a target set by the pressures (Physics.social), so
                // a gauge nudge is TRANSIENT by construction — which is why "health" was promised
                // four times in one run and never moved off `strained 3/5`. The durable half of any
                // social effect is the pressure, which is now on the line beside it.
                //
                // ", for a year or two" used to be appended to EVERY gauge movement to mark it as
                // transient. On a seven-option screen that is the same eight words eight times, and
                // the phrase was the single most repeated string in the game. It is dropped: the
                // distinction it drew is not one the player can act on option by option, whereas
                // the ceiling and floor notes are, and those stay.
                val have = s.gauge[k]
                val note = when {
                    d > 0 && have != null && have >= 99.0 -> " (already at its ceiling)"
                    d > 0 && have != null && 100.0 - have < d * 0.5 -> " (barely any room left)"
                    d < 0 && have != null && have <= 1.0 -> " (already at its floor)"
                    else -> ""
                }
                put(d < 0, size + way + (GAUGE_PRICE_NAMES[k] ?: gaugeLabels[k] ?: k) + note)
                chip(d < 0, (gaugeLabels[k] ?: k) + note,
                    dots = if (abs(d) < 6) 1 else if (abs(d) < 12) 2 else 3, kind = "gauge")
            } ?: Regex("""^pressure\.([A-Za-z]+)\s*(\+=|-=)\s*(-?[0-9.]+)$""").find(e)?.let {
                // THE LARGEST SILENT DEFECT FOUND IN THE 2026-08-31 PLAYTESTS.
                //
                // 456 of ~1500 effects in the corpus are pressure movements and NONE of them
                // reached the screen, because pressures are designer-view-only state. The result:
                // 39 of 410 options rendered as `costs: 30 food` with no gains line at all -- a
                // pure fine whose entire payoff was invisible. Readers called out an option they
                // "would never take in any state I can imagine" 58 times across 82 decisions, and
                // every existing guard passed those options clean.
                //
                // The 0..1 VALUE stays hidden -- that is load-bearing, because pressures reach the
                // player as symptom events and the two-warnings interlock telegraphs 100% of
                // hidden-cause crises. What is shown is the DIRECTION, in the same "named, never
                // numbered" register the gauges already use ("a good deal of morale", never "43").
                val (k, op, v) = it.destructured
                val d = v.toDouble() * (if (op == "-=") -1 else 1)
                val w = pressureWords[k]
                if (w != null) {
                    val size = if (abs(d) >= 0.15) "a good deal " else ""
                    put(d > 0, size + (if (d < 0) w.first else w.second))
                    chip(d > 0, if (d < 0) w.first else w.second,
                        dots = if (abs(d) < 0.08) 1 else if (abs(d) < 0.18) 2 else 3, kind = "strain")
                }
            } ?: Regex("""^kill:([0-9.]+)$""").find(e)?.let {
                // "1 lives" reached the player five times on one screen (playtest 05 D65).
                val n = it.destructured.component1().toDouble()
                put(true, fmtQty(n) + if (n < 1.5) " life" else " lives")
                chip(true, fmtQty(n) + if (n < 1.5) " life" else " lives", kind = "lives")
            } ?: Regex("""^damage:(\S+)\s+(-?[0-9.]+)$""").find(e)?.let {
                val (m, amt) = it.destructured
                val name = Content.module(m)?.name?.lowercase() ?: m
                put(!amt.startsWith("-"), (if (amt.startsWith("-")) "repairs the " else "wear on the ") + name)
                chip(!amt.startsWith("-"), (if (amt.startsWith("-")) "repairs " else "wear on ") + name, kind = "machine")
            } ?: run {
                // Without instruments a year in orbit still rules the worst ground out, and that is
                // the whole of what the option buys: a flag has no price of its own, so it is named.
                if (e.startsWith("flag:site_watched") && !Expr.cond(s, emptySet(), "flag:site_watched")) {
                    gains.add("a blind landing goes badly 1 in 10 times, not 3 in 10")
                    chip(false, "safer landing: 1 in 10 goes badly, not 3", kind = "knowledge")
                }
                // An ambition is the one payoff that lands a decade later, and the card showed
                // "Fund it" as "✖ power 6 kW · ✚ morale ●" beside a funded-and-taught variant with
                // strictly more on it: the largest gain in the corpus, visible only on a tap.
                if (e.startsWith("ambition:")) AMBITIONS.firstOrNull { it.id == e.removePrefix("ambition:").trim() }?.let { a ->
                    chip(false, "pays off in ${a.years} years", kind = "ambition")
                }
                if (e.startsWith("install:")) {
                    val m = Content.module(e.removePrefix("install:").trim())?.name?.lowercase()
                        ?: e.removePrefix("install:").trim()
                    gains.add("builds the $m"); chip(false, "builds the $m", kind = "machine")
                }
                if (e.startsWith("grant:")) {
                    val v = Content.capVerb(e.removePrefix("grant:").trim())
                    gains.add("from then on you can $v"); chip(false, "you can $v", kind = "ability")
                }
                if (e.startsWith("revoke:")) {
                    val v = Content.capVerb(e.removePrefix("revoke:").trim())
                    pays.add("you could no longer $v"); chip(true, "no longer $v", kind = "ability")
                }
            }
        }
        return pays to gains
    }

    /**
     * "3 in 10: ✖ spares 30 · ✖ morale ●" — each branch of a real gamble, with its odds and price.
     *
     * "🎲 gamble" alone told a first-time player that something was uncertain and nothing about
     * what: the blind landing's 3-in-10 chance of re-founding half the base on rock was the whole
     * cost of the option, and it was invisible until the dice had rolled.
     */
    private fun oddsOf(s: WorldState, live: List<Outcome>): List<String> {
        val total = live.sumOf { it.p }.takeIf { it > 0 } ?: return emptyList()
        return live.sortedByDescending { it.p }.map { oc ->
            priceOf(s, Option(label = "", hint = "", effects = oc.effects), showHave = false)
            val tenths = (oc.p / total * 10).roundToInt().coerceIn(1, 9)
            val price = lastChips.sortedBy { if (it.good) 1 else 0 }.joinToString(" · ") { c ->
                (if (c.kind == "lives") "☠ " else if (c.good) "✚ " else "✖ ") + c.text +
                        (if (c.dots > 0) " " + "●".repeat(c.dots) else "")
            }
            "$tenths in 10: " + price.ifEmpty { "nothing you can measure" }
        }
    }

    /** The chips [priceOf] produced for the option it was last called on. */
    private val lastChips = mutableListOf<Chip>()

    /** "4 days", "2 wk", "3 mo", "4½ y": a quantity of supply, as time at today's headcount. */
    private fun supplyTime(months: Double): String {
        val days = months * 30.4
        return when {
            days < 1.0 -> "under a day"
            days < 14.0 -> "${days.roundToInt()} " + if (days.roundToInt() == 1) "day" else "days"
            months < 2.0 -> "${(days / 7).roundToInt()} wk"
            months < 24.0 -> "${months.roundToInt()} mo"
            else -> {
                val halves = (months / 6.0).roundToInt()
                (halves / 2).toString() + (if (halves % 2 == 1) "½" else "") + " y"
            }
        }
    }

    /**
     * What the situation does whatever is chosen, for the conditions the engine runs down on its
     * own: an empty larder and a failing grid. There an option's own price is a small share of
     * what the year will cost, and saying only that share is how "1 life" came to mean 57.
     */
    private fun stakesOf(s: WorldState): String {
        // Empty stores kill every month whether or not the trays still grow something: a colony
        // that "grows" 60% of what it eats buried 58 of 169 in the playtest that found this.
        return when {
            s.food < 1.0 ->
                "Whatever you choose, hunger will kill people every month until food comes back. " +
                        "A cost in lives below is on top of that."
            Physics.powerRatio(s) < 0.45 ->
                "Whatever you choose, the grid cannot carry the colony, and it gets worse every month."
            else -> ""
        }
    }

    /** "full - the shop makes 28/yr" — what a spend of spares actually costs, in time. */
    private fun sparesNote(s: WorldState): String {
        val perYear = Physics.sparesPerYear(s)
        val cap = Physics.sparesCap(s)
        val atCap = s.spares >= cap - 0.5
        val rate = if (perYear >= 1.0) "the shop makes ${fmtQty(perYear)}/yr"
                   else "nothing here makes spares"
        // No em dash inside the note: the stores line already separates the value from the note
        // with one, and "spares 450 — full — the shop makes 17/yr" reads as a dropped clause.
        return when {
            atCap -> "full, and $rate"
            else -> "not running down, and $rate"
        }
    }

    /** "the lab makes 6/yr", or the fact that nothing here makes any. */
    private fun medicineNote(s: WorldState): String {
        val perYear = Physics.medicinePerYear(s)
        val rate = if (perYear >= 0.5) "the lab makes ${fmtQty(perYear)}/yr"
                   else "nothing here makes more"
        return "not running down, and $rate"
    }

    /** "45", "2.5", "a" — quantities the player can compare with the stores line. */
    private fun fmtQty(v: Double) = when {
        v >= 10 -> Fmt.int(v)
        v == v.roundToInt().toDouble() -> Fmt.int(v)
        else -> Fmt.dec(v, 1)
    }

    /** The words the stores line already uses. A price in units nobody displays is not a price. */
    private val resUnits = mapOf(
        "food" to "food", "water" to "water", "spares" to "spares", "medicine" to "medicine",
        "deltaV" to "km/s of spare fuel", "cultivated" to "m² of growing area",
        "data" to "knowledge")

    private fun readEffect(raw: String): String? {
        val e = raw.trim()
        Regex("""^schedule:([A-Za-z0-9_]+)\s+in\s+([0-9]+)(?:\.\.([0-9]+))?y$""").find(e)?.let {
            val (id, lo, hi) = it.destructured
            val title = Content.event(id)?.title ?: id
            return "later: '" + title + "' in " + lo + (if (hi.isEmpty()) "" else "-" + hi) + "yr"
        }
        Regex("""^flag:([A-Za-z0-9_]+)\s*=\s*true(?:\s+for\s+([0-9]+)y)?$""").find(e)?.let {
            val (n, dur) = it.destructured
            return "remembers: " + n + (if (dur.isEmpty()) "" else " (" + dur + "yr)")
        }
        Regex("""^flag:([A-Za-z0-9_]+)\s*=\s*false$""").find(e)?.let {
            return "forgets: " + it.destructured.component1()
        }
        Regex("""^kill:([0-9.]+)$""").find(e)?.let { return it.destructured.component1() + " die" }
        Regex("""^damage:(\S+)\s+(-?[0-9.]+)$""").find(e)?.let {
            val (m, amt) = it.destructured
            val name = Content.module(m)?.name ?: m
            return (if (amt.startsWith("-")) "repairs " else "damages ") + name
        }
        Regex("""^ambition:([A-Za-z0-9_]+)$""").find(e)?.let {
            val a = AMBITIONS.firstOrNull { d -> d.id == it.destructured.component1() }
                ?: return "starts unknown undertaking " + it.destructured.component1()
            // The whole point of the designer view here: an ambition's payoff arrives a decade
            // later, so "what would this produce" has to include what it produces THEN.
            return "starts '" + a.title + "' \u2014 " + a.years + "yr at " +
                    a.sparesPerYear.toInt() + " spares/yr, then " +
                    a.reward.mapNotNull { r -> readEffect(r) }.joinToString(" \u00b7 ")
        }
        if (e.startsWith("phase:")) return "moves to " + e.removePrefix("phase:").lowercase()
        if (e.startsWith("thread.var:")) return "note: " + e.removePrefix("thread.var:")
        // `res.energy` is a power COMMITMENT, not a stock (D008), and "energy -6" read as a stock
        // for a whole session of playtesting. Say which way the power is going.
        Regex("""^res\.energy\s*(\+=|-=)\s*(-?[0-9.]+)$""").find(e)?.let {
            val (op, v) = it.destructured
            return v + " kW " + (if (op == "-=") "committed" else "freed")
        }
        Regex("""^(res|pop|gauge|pressure)\.([A-Za-z]+)\s*(\+=|-=|=)\s*(-?[0-9.]+)$""").find(e)?.let {
            val (ns, key, op, v) = it.destructured
            val sign = if (op == "-=") "-" else if (op == "+=") "+" else "="
            // Hidden state is bracketed so it is obvious at a glance which numbers the player
            // can never see for themselves.
            return (if (ns == "pressure") "[" + key + "]" else key) + " " + sign + v
        }
        return e
    }

    /**
     * Direction over the last few years, which is what makes a number actionable. A player who can
     * see "spares 140, falling four years" forms the goal themselves; one who sees only "spares 140"
     * has to be told.
     */
    /**
     * ONE QUESTION, ONE ANSWER. The note says how long this lasts; the trend says which way it is
     * going; neither ever repeats or contradicts the other.
     *
     * This was two independent computations of the same fact printed side by side — the note from
     * `Physics.runsOut`, the trend from a five-year percentage change — and they disagreed
     * constantly. Twelve reviewers in one playtest and eight in the other stopped on the same
     * strings: `food 0.0 [holding] (falling) LOW`, `food 626 [rising] (rising) LOW`,
     * `medicine 42 [9 yr left]` above `medicine falling 7 yrs`. One of them called it *"two
     * different countdowns for one stock on adjacent lines"*, which is exactly what it was.
     *
     * The trend is now derived FROM the depletion clock rather than beside it, so there is only one
     * arithmetic in play.
     */
    private fun stock(label: String, idx: Int, now: Double, fallback0: String, low: Boolean,
                      counted: Boolean = false): StockView {
        // A COUNTED STOCK LEADS WITH ITS COUNT. Options price spares and medicine as numbers
        // ("✖ spares 20"), and the line above them said only "spares: not running down", so a
        // first-time player could not tell whether 20 was a scratch or the lot. Food and water
        // are priced as time, and their notes already are time.
        fun counted(n: String) = if (!counted || n == "empty") n
                                 else fmtQty(now) + (if (n.isEmpty()) "" else ", $n")
        val fallback = counted(fallback0)
        val h = state.yearly
        if (h.size < 3) return StockView(label, now, fallback, low)
        val past = h[max(0, h.size - 5)][idx]
        val change = if (past <= 0.001) 0.0 else (now - past) / past
        var years = 0
        var i = h.size - 1
        val falling = now < past
        while (i > 0 && ((h[i][idx] < h[i - 1][idx]) == falling)) { years++; i-- }
        val dir = when { change < -0.12 -> -1; change > 0.12 -> 1; else -> 0 }
        val left = Physics.runsOut(state, idx, now)
        // The note answers "how long does this last?" and nothing else. When there is no depletion
        // date it stays EMPTY rather than inventing a second word for the arrow — "holding" beside
        // a falling arrow is the interface arguing with itself.
        val note = when {
            now <= 0.001 -> "empty"
            left == null -> fallback
            left < 1.0 -> "gone within the year"
            left < 2.0 -> "about a year left"
            left < 40.0 -> "" + left.roundToInt() + " years left"
            // Past forty years the clock is noise: it is set by a rounding error in a five-year
            // window, and `spares 371 [50 yr left]` printed with one year to run because of it.
            else -> fallback
        }
        // "rising 2 yrs" was read as a quantity ("two years of what?"). It is a duration, and
        // the preposition is what says so.
        val trendTxt = when {
            dir == 0 -> ""
            years >= 2 -> (if (dir < 0) "falling for " else "rising for ") + years + " years"
            else -> if (dir < 0) "falling" else "rising"
        }
        // Low means "this runs out inside a decade", whatever its absolute size: 500 spares is
        // comfortable at one a year and an emergency at eighty. The caller's own test still counts,
        // because a stock can sit still at three months' worth and that is an emergency too.
        return StockView(label, now, if (note == fallback) note else counted(note),
            low || (left != null && left < 10.0), dir, trendTxt)
    }

    /**
     * "14 months' margin" — how long the reserve covers the loop's losses at the current rate.
     *
     * A closed loop has no depletion date, so `Physics.runsOut` correctly returns nothing and the
     * cell was blank for the whole century. But the reserve still means something: it is what the
     * colony has if the loop degrades, and every water event in the corpus is about exactly that.
     */
    private fun waterMargin(s: WorldState): String {
        if (s.water <= 0.01) return "empty"
        val h = s.yearly
        // "in reserve" for the first three years, and then whenever the loop is not losing anything,
        // told a playtester nothing at all: *"I see water 95 and I have no idea what 95 means."*
        // A closed loop has no depletion date, which is exactly why the reserve needs its OWN
        // denominator: what it covers if the recycling stops, which is what every water event in
        // the corpus is about.
        val drinking = max(1.0, s.popTotal) * Physics.WATER_PER_PERSON_MONTH
        val ifItStops = (s.water / drinking).roundToInt()
        val held = "$ifItStops months of drinking if the loop stops"
        if (h.size < 3) return held
        val lossPerYear = (h[max(0, h.size - 4)][1] - s.water) / 3.0
        if (lossPerYear <= 0.01) return held
        val months = (s.water / lossPerYear * 12).roundToInt()
        return if (months >= 240) held else "$months months' margin, losing as it is"
    }

    /** The month the player was last asked something, so a gap can be described rather than implied. */
    private var lastShownMonth = -1

    private fun gapWords(month: Int): String {
        if (lastShownMonth < 0) return ""
        // A BARE DURATION, so the header can say what it is a duration OF. It used to read
        // "a year later", which sits two lines above the repeat preamble's "46 years ago you faced
        // this" — a different clock measuring a different thing — and 11 readers across four runs
        // quoted the pair back as a contradiction.
        val d = month - lastShownMonth
        return when {
            d <= 0 -> "no time at all"
            d == 1 -> "a month"
            d < 12 -> "$d months"
            d < 24 -> "a year"
            else -> "${d / 12} years"
        }
    }

    private fun colonyView(s: WorldState): ColonyView {
        val pop = s.popTotal
        val months = if (pop > 0) s.food / pop else 0.0
        val gen = Physics.powerGen(s); val draw = Physics.powerDraw(s)
        val r = gen / draw
        return ColonyView(
            shipName = s.build.name,
            year = s.year,
            monthOfYear = s.month % 12 + 1,
            place = when (s.phase) {
                // "the target" was the vocabulary of a mission plan, not of the people in the ship:
                // by the time you are looking at it out of a window it is a WORLD you are in orbit
                // above, and nothing has been committed to yet.
                // The corpus names a world "Ochre, 0.38 g" — a label for a mission planner, with
                // the headline parameter welded on. In a sentence it needs to be a place.
                Phase.SPACE -> if (s.hasFlag("arrived")) "in orbit above ${s.planet.shortName}, nothing committed yet"
                               else "in transit to ${s.planet.shortName}"
                Phase.ORBIT -> "in orbit above ${s.planet.shortName}"
                Phase.SURFACE -> "on ${s.planet.shortName}"
            },
            // Between arrival and the first settled season the planet IS the decision — arrival,
            // then what gets done first, then the first night. The readout used to vanish the
            // moment the phase changed, which is exactly when "what did we land on?" starts
            // mattering most.
            atPlanet = s.hasFlag("arrived") &&
                    (s.phase != Phase.SURFACE || !s.hasFlag("first_night_done")),
            // Rounded parts, and a total that is their SUM. Rounding each of four numbers
            // independently produced "134 souls / 60 adult / 55 children / 20 elders" often enough
            // that six reviewers across two runs counted on their fingers and reported a bug.
            souls = s.adults.roundToInt() + s.children.roundToInt() + s.elders.roundToInt(),
            adults = s.adults.roundToInt(),
            children = s.children.roundToInt(),
            elders = s.elders.roundToInt(),
            asleep = s.cryo.roundToInt(),
            stocks = listOf(
                stock("food", 0, s.food, "${months.roundToInt()} months' worth", months < 12),
                // Water had no buffer readout at all: food says "72 months' worth" and water said
                // nothing, so `gains: 30 water` was unevaluable — 30 of what, against what? The
                // denominator is the loop's own shortfall, which is what the reserve is FOR.
                stock("water", 1, s.water, waterMargin(s), s.water < pop * 2),
                // A price is only a price against an income. `spares` sat at exactly its storage
                // cap for twelve consecutive screens in iteration 2 while options charged 10, 20
                // and 45 of it — arithmetically correct, and completely unreadable, because the
                // fallback note was the empty string. Now it says what the shop makes in a year, so
                // a 45-spare cost reads as roughly a year and a half of work.
                stock("spares", 2, s.spares, sparesNote(s), s.spares < 20, counted = true),
                // Same question as spares, and it had no answer at all: "medicine 42" against
                // options that spend 8 of it, with nothing on screen saying whether anything aboard
                // makes any. Ten of the twelve builds carry no laboratory and never make a dose.
                stock("medicine", 5, s.medicine, medicineNote(s), s.medicine < 5, counted = true)
            // A stated cost has to fall on a stock the player can watch. `res.deltaV` was spent by
            // three options and displayed nowhere in play — it existed only on the setup screen's
            // budget page — and then shown only when non-zero, which is precisely backwards: zero
            // is when an option is locked on it ("needs propellant of 2 — you have 0") and the
            // player most needs to see that the ship has no manoeuvring reserve at all.
            // NOT "propellant": what got the expedition here and what lands it is committed and
            // unmanaged. `deltaV` is only the DISCRETIONARY reserve — fuel to change your mind
            // with — and only the nuclear stage carries any, so ten of the twelve builds have
            // none. Calling it "propellant" invited the obvious question of how a ship that flew
            // to Mars has no fuel.
            // On the ground it is not manoeuvring reserve any more: a propellant plant makes it out
            // of the local air and ice, at about one ascent load every three years, and an event
            // asks what the colony wants to do with a full tank. So the line has to say what a
            // full tank IS, and be watchable while it fills.
            , StockView("spare fuel", s.deltaV,
                // "all of it is committed to arriving" is PRE-LANDFALL copy, and it kept printing
                // for the rest of the century. Eight consecutive reviewers in one run stopped on
                // it in years 80-100, and on D68 it stopped being cosmetic: an event offered to
                // "change course" using "the spare fuel, which you were saving for the arrival"
                // to a colony that had been on the ground for ninety-six years.
                if (s.deltaV < 0.01 && s.phase == Phase.SURFACE)
                    "none, and nothing left to leave the ground with"
                else if (s.deltaV < 0.01) "none, all of it committed to arriving"
                else if (s.phase == Phase.SURFACE && Physics.capabilities(s).contains("ISRU_FUEL"))
                    "km/s made here, and one ascent needs 2"
                else "km/s to manoeuvre with",
                // Not low at zero: ten of the twelve builds launch with none and are meant to.
                // `spare fuel 0.0 LOW` beside a gloss saying "all of it is committed to arriving"
                // is a warning about nothing, and six reviewers stopped on it. It is a shortage
                // only once the colony can MAKE fuel and has not yet made an ascent's worth.
                s.phase == Phase.SURFACE && Physics.capabilities(s).contains("ISRU_FUEL") &&
                        s.deltaV < 2.0)
            ),
            powerMade = gen, powerDrawn = draw, powerCommitted = s.powerCommit,
            powerNote = when { r < 0.6 -> "failing"; r < 0.85 -> "brownouts"; r < 1.2 -> "tight"; else -> "comfortable" },
            powerShort = r < 1.0,
            gauges = GAUGES.map { k ->
                val v = s.gauge[k]!!
                val i = ((v / 100.0) * 5).toInt().coerceIn(0, 4)
                GaugeView(k, gaugeLabels[k] ?: k, i, 5, bands[k]!![i], GAUGE_MEANS[k] ?: "")
            },
            cultivated = s.cultivated,
            shortHanded = Physics.labourIndex(s) < 0.9,
            labourPct = (Physics.labourIndex(s) * 100).roundToInt(),
            capabilities = Physics.capabilities(s).map { Content.capShort(it) }.sorted(),
            broken = s.installed.filter { (s.moduleHealth[it] ?: 1.0) <= 0.25 }
                .mapNotNull { Content.module(it)?.name },
            shelterFor = Physics.shelterFor(s).toInt(),
            surveyData = s.data,
            pressures = PRESSURES.map { it to s.pressure[it]!! },
            // Only what the fiction has already surfaced, and only as a rung. A pressure the colony
            // has never shown the player stays invisible; one that has produced a symptom becomes
            // something they can watch, which is what makes an option priced in it answerable.
            strains = PRESSURES.filter { s.symptomWarnings[it]?.isNotEmpty() == true }
                .map { k ->
                    val v = s.pressure[k]!!
                    val i = (v * 5).toInt().coerceIn(0, 4)
                    GaugeView(k, pressureNouns[k] ?: k, i, 5, STRAIN_RUNGS[i])
                }
                .sortedByDescending { it.band },
            axes = AXES.keys.map { it to s.axis(it) },
            tension = PRESSURES.map { p ->
                val lvl = s.pressure[p]!!
                if (lvl < 0.12) 0.0 else lvl.pow(1.6)
            }.sortedDescending().take(3).sum()
        )
    }

    /**
     * A module's passive physics, in words. The screen printed raw keys — `also: deltaV 55` — which
     * is the authoring mini-language leaking onto the player's side of the wall, and A5's
     * "module trivia" anti-pattern exactly: it asks the player to know our catalogue rather than to
     * reason about their ship. The MVP's stated purpose for this screen is that it says which VERB
     * a module grants and which physics it quietly changes; the second half has to be readable.
     */
    private fun plainModifier(key: String, v: Double): String = when (key) {
        "deltaV" -> fmt(v) + " km/s of spare fuel, beyond what it takes to arrive"
        "shelterFor" -> "shelters " + v.toInt() + " people from radiation"
        "shielding" -> "cuts the radiation dose"
        "growArea" -> fmt(v) + " m² of growing area"
        "surfaceGrowArea" -> fmt(v) + " m² of growing area, once you are on the ground"
        // Food is PERSON-months: 2,500 of them is a year for two hundred people or a decade for
        // twenty, and saying "months of food" invites the player to read it as calendar months.
        "foodStore" -> "holds " + fmt(v) + " person-months of food"
        "waterStore" -> "holds " + fmt(v) + " of water"
        "sparesStore" -> "holds " + fmt(v) + " of spares"
        "medStore" -> "holds " + fmt(v) + " of medicine"
        "medOut" -> "makes medicine, slowly"
        "sparesOut" -> "makes spares faster"
        "waterIn" -> "extracts water from outside"
        "waterRecovery" -> "recovers more of the water that is used"
        "yield" -> "a little more out of every crop"
        "biodiversity" -> "more variety in the trays, which fails less completely"
        "durability" -> "harder to break"
        "fertility" -> "makes people readier to have children"
        "spinGravity" -> "gives people weight"
        "sterilisation" -> "kills biology on a surface"
        else -> key + " " + fmt(v)
    }

    private fun moduleViews(s: WorldState): List<ModuleView> =
        s.installed.mapNotNull { Content.module(it) }.sortedBy { it.name }.map { mod ->
            val h = s.moduleHealth[mod.id] ?: 1.0
            ModuleView(
                name = mod.name,
                condition = when { h <= 0.25 -> "broken"; h < 0.8 -> "worn"; else -> "ok" },
                complexity = mod.complexity,
                grants = mod.grants.map { capVerb(it) },
                effects = mod.modifiers.entries.map { plainModifier(it.key, it.value) } +
                        listOfNotNull(
                            if (mod.powerGen > 0) "makes ${fmt(mod.powerGen)} kW" else null,
                            if (mod.powerDraw > 0) "draws ${fmt(mod.powerDraw)} kW" else null
                        )
            )
        }

    private fun budgetViews(s: WorldState): List<BudgetView> {
        val pop = max(1.0, s.popTotal)
        val need = max(1.0, s.build.arrivalYear.toDouble())
        val stored = s.food / (pop * 12.0)
        val grown = s.cultivated * Physics.YIELD_PER_M2 * 12.0 / pop
        val faults = expectedFaults(s)
        return listOf(
            BudgetView("Food for the crossing", stored + grown * need, need, "yr",
                stored + grown * need >= need, "you arrive hungry, or you do not arrive"),
            BudgetView("Power margin", Physics.powerGen(s) / Physics.powerDraw(s), 1.0, "x",
                Physics.powerGen(s) >= Physics.powerDraw(s),
                "brownouts, and a brownout takes capabilities away"),
            BudgetView("Spares vs expected faults", s.spares / max(1.0, faults * 25), 1.0, "x",
                s.spares >= faults * 25, "faults stop being repairable and start being permanent"),
            // Two kilometres a second is what a targeted descent costs: below that the expedition
            // arrives and lands on whatever is beneath it. That is a launch decision, so it belongs
            // on the launch screen rather than being discovered in orbit.
            BudgetView("Spare fuel to choose a site", s.deltaV, 2.0, "km/s", s.deltaV >= 2.0,
                "you land on whatever is beneath you when you arrive")
        )
        // There was a fourth, "human lifetime vs crossing". It was hardcoded to always pass, so it
        // was a bar that was always full: no information, and actively misleading as a thing to
        // check. It is also the wrong question now that children are born during the crossing —
        // a long voyage does not run out of humans, it replaces them.
    }

    private fun expectedFaults(s: WorldState): Double =
        s.installed.sumOf { Content.module(it)?.complexity ?: 0.0 } *
                0.0005 * 12 * 100 * (1.35 - 0.7 * s.build.testing)

    private fun crewNotes(s: WorldState): List<String> = buildList {
        add("People are not only mouths. Effective labour scales with morale, health, unity and " +
            "dissent — and labour is what feeds everyone and keeps the machines running.")
        add("Labour right now: ${(Physics.labourIndex(s) * 100).roundToInt()}% of what this many " +
            "people should manage.")
        if (s.cryo > 0) add("Sleepers cost power, not food — and cannot vote, work, repair or object.")
    }

    private fun setupView(c: Catastrophe): SetupView {
        val s = state
        val f = expectedFaults(s)
        return SetupView(
            motiveName = c.name, motiveLine = c.line, motiveDetail = c.detail,
            missionLine = "settle ${s.planet.name}",
            // Measured facts only. "water 35%" was the planet's TRUE value, printed on the card
            // directly above the planet table's "accessible water 51 ± 22 %, estimated": the
            // setup sheet was leaking what the colony is supposed to find out, and contradicting
            // itself while it did.
            planetNote = "gravity ${fmt(s.planet.gravity)}g · ${fmt(s.planet.temp - 273)}°C",
            shipName = s.build.name, shipNote = s.build.note,
            crossingYears = s.build.arrivalYear,
            testingPct = (s.build.testing * 100).roundToInt(),
            expectedFaultsLow = f * 0.7, expectedFaultsHigh = f * 1.4,
            modules = moduleViews(s), budgets = budgetViews(s), crewNote = crewNotes(s)
        )
    }

    /**
     * Success is viability, not a pulse — the designer's steer (D005 §2b): "the ultimate player goal
     * is to setup a colony which 2 people hardly can build."
     */
    private fun endingView(): EndingView {
        val s = state
        val pop = s.popTotal
        val monthsOfFood = s.food / max(1.0, pop)
        val grows = s.cultivated * Physics.YIELD_PER_M2 >= pop * 0.95
        val held = s.peakPop < 1 || pop >= s.peakPop * 0.8
        val (tier, note) = when {
            // NOT `endReason` again: the frontends print "the colony ended: <endReason>" on the
            // next line, so a LOST ending read "LOST  no future" over "The colony ended: no
            // future" — the same two words twice, and neither of them said what happened.
            !s.alive -> "LOST" to when (s.endReason) {
                "extinction" -> "Nobody is left."
                "no future" -> "Too few people and no children. The last of them grew old here."
                "systems failure" -> "The power never came back, and everything else depended on it."
                "starvation" -> "The stores ran out and nothing grew fast enough to replace them."
                "collapse" -> "A fraction of what it had been, and not recovering."
                else -> "It did not hold."
            }
            pop >= 30 && grows && monthsOfFood > 18 && s.gauge["health"]!! > 50 && held ->
                "THRIVING" to "It could survive losing everyone who remembers Earth."
            pop >= 12 && grows && monthsOfFood > 6 ->
                "ESTABLISHED" to "A colony. One bad decade from trouble, but a colony."
            else -> "CLINGING" to "Alive. Not yet a place that can keep itself."
        }
        val identity = AXES.keys.map { it to s.axis(it) }
            .sortedByDescending { abs(it.second) }.take(4)
            .map { (name, v) ->
                val (pos, neg) = AXES[name]!!
                // Was 0.5 / 0.25, which printed "decisively" four times out of four in every run
                // long enough to matter — a reader called it a stuck dial, and they were right: an
                // axis saturates over seventy tagged decisions. Spread so the words distinguish.
                val strength = when {
                    abs(v) > 0.80 -> "decisively"; abs(v) > 0.55 -> "firmly"
                    abs(v) > 0.30 -> "mostly"; else -> "somewhat"
                }
                strength to (if (v >= 0) pos else neg)
            }
        return EndingView(tier, note, s.alive, s.endReason, s.year, pop.roundToInt(),
            s.trace.count { it.kind != "SYMPTOM" }, identity, epilogue(), evidenceFor(identity))
    }

    /**
     * WHAT ACTUALLY HAPPENED, from the record rather than from a template.
     *
     * A colony that starved forty-two of its ninety-eight people in year 53 was told "ESTABLISHED"
     * and "decisively humane" and nothing else. The identity words are the *character* of a
     * century; they are not an account of it, and a reader who has just spent a hundred years there
     * can tell the difference. Everything below is read off `yearly` and the trace, so it cannot
     * praise a run that did not happen.
     */
    private fun epilogue(): List<String> {
        val s = state
        val out = mutableListOf<String>()
        val h = s.history
        if (h.size >= 2) {
            val peak = h.maxByOrNull { it.second }!!
            if (peak.second > s.popTotal * 1.15)
                out.add("The colony was largest in year ${peak.first}: ${peak.second.roundToInt()} people. " +
                        "It ended with ${s.popTotal.roundToInt()}.")
            // The worst single year, and what it actually was. A famine is not a mood.
            var worst = -1; var worstLoss = 0.0
            for (y in 1 until h.size) {
                val loss = h[y - 1].second - h[y].second
                if (loss > worstLoss) { worstLoss = loss; worst = y }
            }
            if (worst > 0 && worstLoss >= max(3.0, h[worst - 1].second * 0.08))
                out.add("The worst year was ${h[worst].first}: ${worstLoss.roundToInt()} people died" +
                        (if (h[worst].third < h[worst].second * 1.5) ", with the stores empty." else "."))
        }
        // WHAT IT COST, not only what it reached. *"This screen summarizes state when what I want
        // is an account of what it cost."* These are read off the record rather than templated, so
        // a quiet century prints a short epilogue and a brutal one prints a long one.
        // `deaths` is EVERY death including old age — 176 of them over a century is a colony
        // living and dying normally, and calling those "need not have" was simply false.
        // `deathsNotable` is the ones something killed: hunger, vacuum, an empty pharmacy.
        if (s.deathsNotable >= 3.0)
            out.add("${s.deathsNotable.roundToInt()} of the ${s.deaths.roundToInt()} people who died " +
                    "were killed by something — hunger, vacuum, a shortage somebody had to tell you " +
                    "about. The rest grew old here.")
        else if (s.deaths >= 3.0)
            out.add("${s.deaths.roundToInt()} people died over the century, effectively all of them " +
                    "of age. Very few colonies can say that.")
        // `severityOf` deliberately downgrades an authored CRISIS to NOTABLE when nothing on the
        // panel is actually wrong — that is the fix for a [CRISIS] tag pointing at the wrong thing.
        // But the ENDING was counting the downgraded tag, so a century of emergencies handled well
        // enough that the panel stayed healthy reported as having had none: iteration 2 printed
        // "Not one of the 74 decisions you were brought was an emergency" to a colony that had
        // survived an unidentified pathogen, a seventeen-day blackout and a hull fault that cost a
        // radiation dose. A claim this absolute needs evidence the tag cannot supply, so it is now
        // gated on the two things that cannot be downgraded: violent deaths, and a stock at zero.
        val crises = log.count { it.severity == Severity.CRISIS }
        val everViolent = s.deathsNotable >= 1.0
        out.add(when {
            crises > 0 ->
                "You were called to $crises emergencies and ${log.size - crises} other decisions."
            everViolent ->
                "Nothing you were brought arrived labelled an emergency, and ${s.deathsNotable.roundToInt()} " +
                "people still died of something other than age. The difference between those two " +
                "sentences is the whole job."
            else ->
                "Not one of the ${log.size} decisions you were brought was an emergency. " +
                "That is a kind of achievement, and it is invisible from inside."
        })
        val built = s.installed.filter { !s.build.modules.contains(it) }.mapNotNull { Content.module(it)?.name }
        if (built.isNotEmpty()) out.add("You built what you did not bring: " + built.joinToString(", ") + ".")
        if (s.granted.isNotEmpty()) {
            // "none of which" over a single item read as a sentence that had lost its subject:
            // "able to keep an audited account — none of which it left Earth able to do".
            val verbs = s.granted.map { Content.capShort(it) }
            out.add("The colony ends able to " + verbs.joinToString(", ") +
                    (if (verbs.size == 1) ", which it could not do when it left Earth."
                     else " — none of which it could do when it left Earth."))
        }
        val done = s.ambitions.mapNotNull { a -> AMBITIONS.firstOrNull { it.id == a.id } }
            .filter { d -> (s.ambitions.first { it.id == d.id }.monthsDone) >= d.years * 12 }.map { it.title }
        if (done.isNotEmpty()) out.add("Finished: " + done.joinToString("; ") + ".")
        val broke = s.installed.filter { (s.moduleHealth[it] ?: 1.0) <= 0.25 }.mapNotNull { Content.module(it)?.name }
        if (broke.isNotEmpty()) out.add("Still broken at the end: " + broke.joinToString(", ") + ".")
        return out
    }

    /**
     * For each identity word, the decision that most moved it — quoted, with its year.
     *
     * The axes are written only by the tags on options the player chose, so the record already
     * holds the answer; it was simply never shown. Four unevidenced adverbs read as a stuck dial
     * and as flattery; four with "year 31: you chose 'Give them the schools'" under them are an
     * argument the player can check against their own memory of the run.
     */
    private fun evidenceFor(identity: List<Pair<String, String>>): List<Pair<String, String>> {
        val out = mutableListOf<Pair<String, String>>()
        for ((_, word) in identity) {
            // Which axis pole is this word, and which tag writes it?
            val axis = AXES.entries.firstOrNull { it.value.first == word || it.value.second == word } ?: continue
            val tag = if (axis.value.first == word) axis.value.first else axis.value.second
            // The FIRST such choice: "year 12: you chose X" reads as the moment the colony
            // started becoming this. The most recent one is just the latest instance and tells the
            // player nothing they do not already remember.
            val hit = taggedChoices.firstOrNull { it.third.contains(tag) } ?: continue
            out.add(word to "year ${hit.first}: you chose \u201c${hit.second}\u201d")
        }
        return out
    }

    /** (year, option label, tags) for every decision the player made — the identity ledger's source. */
    private val taggedChoices = mutableListOf<Triple<Int, String, List<String>>>()

    private fun fmt(x: Double) = if (abs(x) >= 10) x.roundToInt().toString() else Fmt.sig2(x)
}
